/**
 * CompanySettings - General company configuration
 *
 * Features:
 * - Company name and branding
 * - Default settings
 * - Processing preferences
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchCompanySettings, updateCompanySettings } from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './CompanySettings.module.css';

export default function CompanySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanySettings();
      setSettings(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateCompanySettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading settings...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className={styles.error}>
        Failed to load settings. Please try again.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Settings</h2>
        <p className={styles.subtitle}>
          General company configuration and preferences
        </p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className={styles.successBanner}>
          Settings saved successfully
        </div>
      )}

      <div className={styles.sections}>
        {/* Company Info */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Company Information</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Company Name</label>
            <input
              type="text"
              className={styles.input}
              value={settings.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Contact Email</label>
            <input
              type="email"
              className={styles.input}
              value={settings.contact_email || ''}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              placeholder="contact@company.com"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Website</label>
            <input
              type="url"
              className={styles.input}
              value={settings.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://company.com"
            />
          </div>
        </section>

        {/* Cropping Preferences */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Cropping Preferences</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Landscape Aspect Ratios</label>
            <div className={styles.ratioOptions}>
              {['3:2', '4:3', '16:9', '1:1'].map((ratio) => {
                const selected = settings.cropping_preferences?.landscape_ratios?.includes(ratio) ?? (ratio === '3:2');
                return (
                  <button
                    key={ratio}
                    type="button"
                    className={`${styles.ratioChip} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const current = settings.cropping_preferences?.landscape_ratios || ['3:2'];
                      const updated = selected
                        ? current.filter(r => r !== ratio)
                        : [...current, ratio];
                      handleChange('cropping_preferences', {
                        ...settings.cropping_preferences,
                        landscape_ratios: updated.length > 0 ? updated : ['3:2'],
                      });
                    }}
                  >
                    {ratio}
                  </button>
                );
              })}
            </div>
            <p className={styles.hint}>
              Available aspect ratios for landscape crops
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Portrait Aspect Ratios</label>
            <div className={styles.ratioOptions}>
              {['2:3', '3:4', '9:16', '1:1'].map((ratio) => {
                const selected = settings.cropping_preferences?.portrait_ratios?.includes(ratio) ?? (ratio === '2:3');
                return (
                  <button
                    key={ratio}
                    type="button"
                    className={`${styles.ratioChip} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const current = settings.cropping_preferences?.portrait_ratios || ['2:3'];
                      const updated = selected
                        ? current.filter(r => r !== ratio)
                        : [...current, ratio];
                      handleChange('cropping_preferences', {
                        ...settings.cropping_preferences,
                        portrait_ratios: updated.length > 0 ? updated : ['2:3'],
                      });
                    }}
                  >
                    {ratio}
                  </button>
                );
              })}
            </div>
            <p className={styles.hint}>
              Available aspect ratios for portrait crops
            </p>
          </div>

        </section>
      </div>

      <div className={styles.footer}>
        <Button variant="ghost" onClick={loadSettings} disabled={saving}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
